import cn from "clsx";
import { Icon } from "react-migration/components/Icon";
import { ComparableDetailSection } from "./ComparableDetailSection";
import {
  ZooplaComparable,
  LastValueSource,
  LastValueType,
  ZooplaValuationHistoryItemDTO,
} from "react-migration/lib/typings/Zoopla";
import { IComparablesTransactionDTO, TransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { formatDate } from "react-migration/domains/comparables/util/Dates";
import { CurrencyCode } from "react-migration/lib/typings/Currency";
import { Tabs, TabsList, TabsTrigger } from "react-migration/components/Tabs";
import { useEffect, useState } from "react";
import { formatToCurrency, getCurrencyFromLicense } from "react-migration/lib/util/numberFormat";
import { ExpandableList } from "react-migration/components/ExpandableList";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Tooltip } from "react-migration/components/Tooltip";
import { Theme } from "react-migration/lib/theme/Theme";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { useSnapshot } from "valtio";
import { userStore, MeasurementSystem } from "src/js/stores/user/store";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";

type TransactionHistoryItem = ZooplaValuationHistoryItemDTO & {
  currency: CurrencyCode;
  differencePercentage: number | null;
  timeOnMarket?: number | null;
  isLatest?: boolean;
  pricePerArea?: number | null;
};

const calcPercentage = (value: number, prevValue: number | null): number | null => {
  if (prevValue === null || prevValue === 0) return null;
  const percentage = ((value - prevValue) / prevValue) * 100;
  return Number(percentage.toFixed(2));
};

const transformTransactionData = ({
  zooplaData,
  ltData,
  isRent = false,
}: {
  zooplaData?: (ZooplaValuationHistoryItemDTO | null)[];
  ltData?: TransactionDTO[];
  isRent?: boolean;
}): TransactionHistoryItem[] => {
  const currency = getCurrencyFromLicense();

  if (zooplaData && zooplaData.length > 0) {
    // Process in chronological order (oldest to newest)
    const chronologicalData = [...zooplaData].sort(
      (a, b) => new Date(a?.valueDate ?? "").getTime() - new Date(b?.valueDate ?? "").getTime()
    );
    let prevValue: number | null = null;

    const result = chronologicalData
      .filter((item): item is ZooplaValuationHistoryItemDTO => item !== null && typeof item.value === "number")
      .map((item) => {
        const transaction = {
          value: item.value,
          valueType: item.valueType as keyof typeof LastValueType,
          valueDate: formatDate(item.valueDate),
          valueSource: item.valueSource as LastValueSource,
          currency: currency as CurrencyCode,
          differencePercentage: calcPercentage(item.value, prevValue),
          timeOnMarket: item.valueType === LastValueType.Listed ? calculateTimeOnMarket(item.valueDate) : null,
          isLatest: item.valueType === LastValueType.Listed ? true : false,
        };
        prevValue = item.value;
        return transaction;
      });
    return result.reverse();
  } else if (ltData && ltData.length > 0 && !isRent) {
    const chronologicalData = [...ltData].sort(
      (a, b) => new Date(a.date_of_transfer).getTime() - new Date(b.date_of_transfer).getTime()
    );
    let prevValue: number | null = null;

    const result = chronologicalData.map((item) => {
      const transaction = {
        value: item.price,
        valueType: LastValueType.Sale as keyof typeof LastValueType,
        valueDate: formatDate(item.date_of_transfer),
        valueSource: "Land Registry" as LastValueSource,
        currency: (item.currency as CurrencyCode) ?? currency,
        differencePercentage: calcPercentage(item.price, prevValue),
      };
      prevValue = item.price;
      return transaction;
    });

    return result.reverse();
  }
  return [];
};

const calculateTimeOnMarket = (listingsDate: string) => {
  if (listingsDate) {
    return Math.floor((new Date().getTime() - new Date(listingsDate).getTime()) / (1000 * 60 * 60 * 24));
  }
  return null;
};

export const ComparableItemTransactionHistory = ({
  record,
  zooplaData,
  loading,
  noFullAccess,
}: {
  record?: IComparablesTransactionDTO;
  zooplaData?: ZooplaComparable;
  loading?: boolean;
  noFullAccess: boolean;
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<"sale" | "rent">("sale");
  const isUSUser = hasFeature(Feature.usAccess);

  const historyItems = zooplaData?.valuation?.[selectedTab]?.history || [];

  const { prev_transactions } = record ?? {};

  const latestTransaction = record
    ? [
        {
          price: record?.price,
          currency: record?.currency,
          date_of_transfer: record?.date_of_transfer,
          transaction_id: record?.transaction_id,
          uprn: record?.uprn,
        },
      ]
    : [];

  const transactions: TransactionHistoryItem[] = transformTransactionData({
    zooplaData: [...historyItems].filter(Boolean) as ZooplaValuationHistoryItemDTO[],
    ltData: [...(prev_transactions || []), ...latestTransaction] as TransactionDTO[],
    isRent: selectedTab === "rent",
  });

  // If there aren't any sale history items in the Zoopla data, but there are rent history items,
  // show the rent tab as the default tab
  useEffect(() => {
    if (
      (zooplaData?.valuation?.sale?.history ?? []).length === 0 &&
      (zooplaData?.valuation?.rent?.history ?? []).length > 0 &&
      !noFullAccess
    ) {
      setSelectedTab("rent");
    }
  }, [zooplaData, noFullAccess]);

  if (loading) return <SkeletonTransactionHistory />;

  return (
    <ComparableDetailSection dataTestId="comparable-detail-transaction-history">
      {isUSUser ? (
        <Timeline transactions={transactions} record={record} />
      ) : (
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full rounded-lg gap-y-2">
          <TabsList className="bg-blue-grey-100 rounded-lg border-none p-[2px]">
            <TabButton value="sale">
              {t("comparables.selection_details.transaction_history.sale")}
              <TabTooltip tooltip={t("comparables.selection_details.transaction_history.sale.tooltip")} />
            </TabButton>
            <TabButton value="rent" disabled={noFullAccess}>
              {t("comparables.selection_details.transaction_history.rent")}
              <TabTooltip tooltip={t("comparables.selection_details.transaction_history.rent.tooltip")} />
            </TabButton>
          </TabsList>
          <div className="flex flex-col p-4 border rounded-lg border-blue-grey-200a">
            {selectedTab === "sale" && <Timeline transactions={transactions} record={record} />}
            {selectedTab === "rent" && <Timeline transactions={transactions} isRent record={record} />}
          </div>
        </Tabs>
      )}
    </ComparableDetailSection>
  );
};

const TabButton = ({ children, value, disabled }: { children: React.ReactNode; value: string; disabled?: boolean }) => (
  <TabsTrigger
    value={value}
    disabled={disabled}
    className={cn(
      "flex flex-row! gap-[5px] items-center justify-center flex-nowrap",
      "rounded-lg",
      "py-1!",
      "px-2!",
      "h-auto",
      "text-blue-grey-600a",
      "font-medium",
      "text-sm",
      "leading-5!",
      "bg-blue-grey-100",
      "data-[state=active]:bg-background-surface",
      "data-[state=active]:shadow-none",
      "data-[state=active]:text-blue-grey-950!",
      "data-[state=active]:border-blue-grey-200a!",
      "data-[state=active]:border!",
      disabled && "opacity-50 cursor-not-allowed"
    )}
  >
    {children}
  </TabsTrigger>
);

const Timeline = ({
  transactions,
  isRent = false,
  record,
}: {
  transactions: TransactionHistoryItem[];
  isRent?: boolean;
  record?: IComparablesTransactionDTO;
}) => {
  return (
    <div className="relative flex flex-col w-full">
      <ExpandableList limit={3} itemsName="transactions" className="gap-0 py-0!" animationDuration={300} bottomFade>
        {transactions.length > 0 ? (
          transactions.map((item, index) => {
            return (
              <div
                key={index}
                role="listitem"
                data-testid="comparable-detail-transaction-item"
                className={cn(
                  "flex flex-row items-stretch justify-start gap-3 h-24",
                  transactions.length < 4 && index === transactions.length - 1 && "h-auto"
                )}
              >
                <TimelineDot
                  transactionType={item.valueType as LastValueType}
                  isLast={index === transactions.length - 1}
                />
                <TimelineItemData item={item} showPricePerArea={index === 0} record={record} />
              </div>
            );
          })
        ) : (
          <NoTransactions isRent={isRent} />
        )}
      </ExpandableList>
    </div>
  );
};

const TimelineDot = ({ transactionType, isLast = false }: { transactionType: LastValueType; isLast: boolean }) => {
  let icon = "";
  let color = "";
  switch (transactionType) {
    case LastValueType.Sale:
      icon = "icon-lt-home-line";
      color = "border-green-200 bg-green-200";
      break;
    case LastValueType.Valuation:
      icon = "icon-lt-line-chart-line";
      color = "border-marketing-purple-200 bg-marketing-purple-200";
      break;
    case LastValueType.AskingPrice:
    case LastValueType.Listed:
      icon = "icon-lt-eye-line";
      color = "border-platform-blue-200 bg-platform-blue-200";
      break;
    default:
      icon = "icon-lt-home-line";
      color = "border-green-200 bg-green-200";
      break;
  }
  return (
    <div className="flex flex-col items-center w-6 dot-container">
      <div className={cn("flex items-center justify-center w-6 h-6 rounded-full p-1", color)}>
        <Icon icon={icon} size="sm" />
      </div>
      <div
        className={cn("flex-1 border", color, {
          hidden: isLast,
        })}
      />
    </div>
  );
};

const TimelineItemData = ({
  item,
  showPricePerArea,
  record,
}: {
  item: TransactionHistoryItem;
  showPricePerArea?: boolean;
  record?: IComparablesTransactionDTO;
}) => {
  const { user } = useSnapshot(userStore);
  const isMetric = user.settings.unit === MeasurementSystem.METRIC;

  const { valueType, value, valueDate, currency, differencePercentage } = item;

  const { t } = useTranslation();
  const areaUnit = t(isMetric ? "comparables.sqm.alt" : "comparables.sqft.alt");

  const percentageColor =
    differencePercentage === 0 || differencePercentage === null
      ? "text-blue-grey-600a"
      : differencePercentage > 0
      ? "text-green-600"
      : "text-red-600";

  return (
    <div className="flex flex-col items-start justify-start w-full gap-y-1">
      <TimelineItemDataRow>
        <div className="flex flex-row items-center justify-start gap-2 text-sm font-medium text-blue-grey-950">
          {valueType === LastValueType.Listed
            ? LastValueType.AskingPrice
            : valueType === LastValueType.Sale
            ? "Sold price"
            : valueType}
          {/* {isLatest && <Tag color={TagColor.DEFAULT} tag="On market" size="sm" />} */}
        </div>
        <span className="text-sm font-normal text-blue-grey-600a">{valueDate}</span>
      </TimelineItemDataRow>
      <TimelineItemDataRow>
        <span className="text-lg font-semibold text-blue-grey-950" data-testid="comparable-detail-transaction-value">
          {formatToCurrency({ amount: value, currency, maximumFractionDigits: 0 })}
        </span>
        {differencePercentage !== null && (
          <span className={cn("text-sm leading-[26px] font-semibold", percentageColor)}>
            {differencePercentage > 0 ? "+" : ""}
            {differencePercentage}%
          </span>
        )}
      </TimelineItemDataRow>
      {showPricePerArea && record?.total_floor_area && record.total_floor_area > 0 && (
        <TimelineItemDataRow>
          <div className="flex flex-row items-center gap-[7px]">
            <span className="text-sm text-blue-grey-600a">
              {formatToCurrency({
                amount: value / record.total_floor_area,
                currency,
                maximumFractionDigits: 0,
              })}
              {areaUnit ? `/${areaUnit}` : ""}
            </span>
          </div>
        </TimelineItemDataRow>
      )}
      {/* {timeOnMarket && (
        <TimelineItemDataRow>
          <div className="flex flex-row items-center gap-[7px]">
            <Icon icon="icon-lt-time-line" size="sm" />
            <span className="text-sm text-blue-grey-600a">
              {timeOnMarket} days on market
            </span>
          </div>
        </TimelineItemDataRow>
      )} */}
    </div>
  );
};

const TimelineItemDataRow = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex flex-row items-center justify-between w-full">{children}</div>;
};

const TabTooltip = ({ tooltip }: { tooltip: string }) => (
  <Tooltip.Wrapper>
    <Tooltip.Trigger asChild>
      <span className="inline-flex">
        <Icon icon="icon-lt-information-line" size="sm" extraClasses="text-blue-grey-600a" />
      </span>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content size="sm" side="bottom" align="end" theme={Theme.Light}>
        <Tooltip.Description>{tooltip}</Tooltip.Description>
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Wrapper>
);

const NoTransactions = ({ isRent }: { isRent: boolean }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center w-full gap-y-1">
      <div className="flex items-center justify-center rounded-full w-11 h-11 bg-blue-grey-100 text-blue-grey-600a">
        <Icon icon="icon-lt-survey-line" size="lg" extraClasses="text-platform-blue-950" />
      </div>
      <div className="flex flex-col items-center justify-center gap-y-2">
        <span className="text-lg font-semibold text-blue-grey-950">
          {t("comparables.selection_details.transaction_history.no_transactions.title")}
        </span>
        <span className="text-sm font-normal text-blue-grey-950">
          {t(`comparables.selection_details.transaction_history.no_${isRent ? "rent" : "sale"}_transactions`)}
        </span>
      </div>
    </div>
  );
};

const SkeletonTransactionHistory = () => {
  return (
    <div className="relative flex flex-col w-full gap-2 gap-y-2">
      <div className="relative h-[32px] w-full">
        <SkeletonLoading rows={1} showPadding={false} />
      </div>
      <div className="relative h-[362px] w-full">
        <SkeletonLoading rows={1} showPadding={false} />
      </div>
    </div>
  );
};
